import { useCallback, useState } from 'react'

import { SecondaryDisplayButtons } from '~/components/txn-display/SecondaryDisplayButtons'
import { StateChangeException } from '~/components/txn-display/state-change/components/StateChangeException'
import { StateChangeList } from '~/components/txn-display/state-change/components/StateChangeList'
import { useBuildPendingStateChangeProps } from '~/components/txn-display/state-change/hooks/useBuildPendingStateChangeProps'
import { useCheckIfQuoteExpired } from '~/components/txn-display/state-change/hooks/useCheckIfQuoteExpired'
import { useRunTenderlySimulation } from '~/components/txn-display/state-change/hooks/useRunTenderlySimulation'
import { useSimulatedStateChanges } from '~/components/txn-display/state-change/hooks/useSimulatedStateChanges'
import { QUOTE_EXPIRED_ERROR_MESSAGE } from '~/components/txn-display/state-change/hooks/useTransactionStateChanges'
import {
  OfframpDisplayData,
  PendingStateChangeDisplayProps,
} from '~/components/txn-display/state-change/types'

export const PendingStateChangeDisplay = ({
  transaction, // for txns that have been created
  txnData, // for txns that have not been created yet
  dataRefreshing,
  bridgeStateChanges,
  stateChangesToHide,
  decodedCalls: precomputedDecodedCalls,
  stateChangeItems: precomputedStateChangeItems,
  isError: precomputedIsError,
  errorMessage: precomputedErrorMessage,
  isSimulationRevert: precomputedIsSimulationRevert,
  hideDetailsButton,
  hideWhenEmpty,
  offrampData,
}: PendingStateChangeDisplayProps & {
  hideDetailsButton?: boolean
  hideWhenEmpty?: boolean
  offrampData?: OfframpDisplayData
}) => {
  const [isShowingCalls, setIsShowingCalls] = useState(false)
  const handleToggleCalls = useCallback(
    () => setIsShowingCalls((prev) => !prev),
    [],
  )

  const { hasError: quoteError, isLoading: isLoadingQuoteExpired } =
    useCheckIfQuoteExpired(transaction)

  const {
    transactionId,
    smartAccountAddress,
    chainId,
    calls,
    addressesToWatch,
    simulationUrl,
  } = useBuildPendingStateChangeProps({ transaction, txnData })

  const runTenderlySimulation = useRunTenderlySimulation({
    transaction,
    txnData,
  })

  // Skip simulation if precomputed values provided (hook short-circuits when calls is undefined)
  const {
    stateChangeItems: fetchedStateChangeItems,
    isLoading: isLoadingStateChanges,
    isError,
    errorMessage: simulationErrorMessage,
    isSimulationRevert: simulationIsRevert,
    decodedCalls: fetchedDecodedCalls,
  } = useSimulatedStateChanges({
    smartAccountAddress,
    chainId,
    calls: precomputedStateChangeItems ? undefined : calls,
    transactionId,
    addressesToWatch,
    bridgeStateChanges,
    stateChangesToHide,
  })

  const stateChangeItems =
    precomputedStateChangeItems ?? fetchedStateChangeItems
  const decodedCalls = precomputedDecodedCalls ?? fetchedDecodedCalls

  const isLoading = isLoadingStateChanges || isLoadingQuoteExpired
  const hasNoTransfers = stateChangeItems.length === 0 && !isLoading
  const hasError = (isError || precomputedIsError || quoteError) && !isLoading
  const hasDecodedCalls = !!decodedCalls?.length
  const isSimulationRevert =
    !quoteError &&
    (precomputedIsError
      ? (precomputedIsSimulationRevert ?? simulationIsRevert)
      : simulationIsRevert)

  const secondaryDisplayButtons = hideDetailsButton ? undefined : (
    <SecondaryDisplayButtons
      isShowingCalls={isShowingCalls}
      onToggleCalls={handleToggleCalls}
      hasDecodedCalls={hasDecodedCalls}
      runTenderlySimulation={runTenderlySimulation}
      simulationUrl={simulationUrl}
    />
  )

  // When hideWhenEmpty is true, don't show anything if there are no transfers and no error
  if (hideWhenEmpty && hasNoTransfers && !hasError) return null

  if (hasError || hasNoTransfers) {
    const errorMessage = quoteError
      ? QUOTE_EXPIRED_ERROR_MESSAGE
      : precomputedIsError
        ? precomputedErrorMessage
        : (simulationErrorMessage ?? precomputedErrorMessage)

    return (
      <StateChangeException
        transaction={transaction}
        txnData={txnData}
        hasNoTransfers={hasNoTransfers}
        hasError={hasError}
        errorMessage={errorMessage}
        isSimulationRevert={isSimulationRevert}
        secondaryDisplayButtons={secondaryDisplayButtons}
      />
    )
  }

  return (
    <StateChangeList
      stateChangeItems={stateChangeItems}
      isLoading={isLoading}
      dataRefreshing={dataRefreshing}
      decodedCalls={decodedCalls}
      isShowingCalls={isShowingCalls}
      secondaryDisplayButtons={secondaryDisplayButtons}
      offrampData={offrampData}
    />
  )
}
